import { SxProps } from '@mui/material'
import chunk from 'lodash/chunk'
import { useMemo, useState, FC } from 'react'
import { PaginationControls as PaginationControlsComponent } from './PaginationControls'

type Input<T> = {
  pageSize: number
  items: T[]
}

type PaginationControlsProps = { hideIfSinglePage?: boolean; sx?: SxProps }

type Output<T> = {
  itemsInCurrentPage: T[]
  PaginationControls: FC<PaginationControlsProps>
  currentPageIndex: number
}

export const usePagination = <T,>({ items, pageSize }: Input<T>): Output<T> => {
  const minPage = 0
  const maxPage = Math.ceil(items.length / pageSize) - 1
  const [page, setPage] = useState<number>(minPage)
  const chunkedItems = useMemo(() => chunk(items, pageSize), [pageSize, items])
  const itemsInCurrentPage = useMemo(() => chunkedItems[page] ?? [], [page, chunkedItems])

  const canGoBack = useMemo(() => page > minPage, [page])
  const canGoForward = useMemo(() => page < maxPage, [page, maxPage])

  const PaginationControls = useMemo<FC<PaginationControlsProps>>(() => {
    const Component: FC<PaginationControlsProps> = ({ hideIfSinglePage = false, sx }) => (
      <PaginationControlsComponent
        minPage={minPage}
        maxPage={maxPage}
        hideIfSinglePage={hideIfSinglePage}
        canGoBack={canGoBack}
        canGoForward={canGoForward}
        setPage={setPage}
        sx={sx}
      />
    )
    return Component
  }, [canGoBack, canGoForward, maxPage])

  return { itemsInCurrentPage, PaginationControls, currentPageIndex: page }
}
